import { Component, OnInit, AfterViewInit, ViewChild, OnDestroy } from '@angular/core';
import { MatLegacyRow as MatRow, MatLegacyTable as MatTable, MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatLegacyMenuTrigger as MatMenuTrigger } from '@angular/material/legacy-menu';
import { MatSort } from '@angular/material/sort';
import { Utils } from '../../../../_helpers/utils';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

import { TranslateService } from '@ngx-translate/core';

import { DaterangeDialogComponent } from '../../../../gui-helpers/daterange-dialog/daterange-dialog.component';
import { IDateRange, DaqQuery, TableType, TableOptions, TableColumn, TableCellType, TableCell, TableRangeType, TableCellAlignType, GaugeEvent, GaugeEventType, TableFilter } from '../../../../_models/hmi';
import { format } from 'fecha';
import { BehaviorSubject, Observable, Subject, of, timer } from 'rxjs';
import { catchError, concatMap, switchMap, takeUntil } from 'rxjs/operators';
import { DataConverterService, DataTableColumn, DataTableContent } from '../../../../_services/data-converter.service';
import { ScriptService } from '../../../../_services/script.service';
import { ProjectService } from '../../../../_services/project.service';
import { SCRIPT_PARAMS_MAP, ScriptParam } from '../../../../_models/script';
import { HmiService } from '../../../../_services/hmi.service';
import { AlarmBaseType, AlarmColumnsType, AlarmPriorityType, AlarmsFilter, AlarmStatusType } from '../../../../_models/alarm';
import { ReportsService } from '../../../../_services/reports.service';
import { ReportColumnsType, ReportFile, ReportsFilter } from '../../../../_models/report';
import * as FileSaver from 'file-saver';
import { CommandService } from '../../../../_services/command.service';
import { LanguageService } from '../../../../_services/language.service';
import { GaugeBaseComponent } from '../../../gauge-base/gauge-base.component';

declare const numeral: any;
@Component({
    selector: 'app-data-table',
    templateUrl: './data-table.component.html',
    styleUrls: ['./data-table.component.scss'],
})
export class DataTableComponent implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild(MatTable, { static: false }) table: MatTable<any>;
    @ViewChild(MatSort, { static: false }) sort: MatSort;
    @ViewChild(MatMenuTrigger, { static: false }) trigger: MatMenuTrigger;
    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
    onTimeRange$ = new BehaviorSubject<DaqQuery>(null);

    rxjsPollingTimer: Observable<number>;
    statusText = AlarmStatusType;
    priorityText = AlarmPriorityType;
    alarmColumnType = AlarmColumnsType;
    reportColumnType = ReportColumnsType;
    loading = false;
    id: string;
    type: TableType;
    isEditor: boolean;
    displayedColumns = [];
    columnsStyle = {};
    dataSource = new MatTableDataSource([]);
    tagsMap = {};
    timestampMap = {};
    tagsColumnMap = {};
    range = { from: Date.now(), to: Date.now() };
    tableType = TableType;
    tableHistoryType = TableType.history;
    lastRangeType = TableRangeType;
    tableOptions = DataTableComponent.DefaultOptions();
    data = [];
    reloadActive = false;
    withToolbar = false;
    private lastDaqQuery = new DaqQuery();
    private destroy$ = new Subject<void>();
    private historyDateformat = '';
    addValueInterval = 0;
    private pauseMemoryValue: TableMapValueDictionary = {};
    setOfSourceTableData = false;
    selectedRow = null;
    events: GaugeEvent[];
    eventSelectionType = Utils.getEnumKey(GaugeEventType, GaugeEventType.select);
    dataFilter: TableFilter | AlarmsFilter | ReportsFilter;

    constructor(
        private dataService: DataConverterService,
        private projectService: ProjectService,
        private hmiService: HmiService,
        private scriptService: ScriptService,
        private reportsService: ReportsService,
        private languageService: LanguageService,
        private commandService: CommandService,
        public dialog: MatDialog,
        private translateService: TranslateService) { }

    ngOnInit() {
        this.dataSource.data = this.data;
        Object.keys(this.lastRangeType).forEach(key => {
            this.translateService.get(this.lastRangeType[key]).subscribe((txt: string) => { this.lastRangeType[key] = txt; });
        });
        this.dataSource.filterPredicate = (match: any, filter: string) => {
            const cells = Object.values(match).map((c: TableCellData) => c.stringValue);
            for (let i = 0; i < cells.length; i++) {
                if (cells[i].toLowerCase().includes(filter)) { return true; }
            }
            return false;
        };

        if (this.isAlarmsType()) {
            Object.keys(this.statusText).forEach(key => {
                this.statusText[key] = this.translateService.instant(this.statusText[key]);
            });
            Object.keys(this.priorityText).forEach(key => {
                this.priorityText[key] = this.translateService.instant(this.priorityText[key]);
            });
            if (this.type === TableType.alarms) {
                this.startPollingAlarms();
            }
        } else if (this.isReportsType()) {
            this.startPollingReports();
        }
    }
    ngAfterViewInit() {
        this.sort.disabled = this.type === TableType.data;
        this.bindTableControls();
    }

    ngOnDestroy() {
        try {
            this.destroy$.next(null);
            this.destroy$.complete();
        } catch (e) {
            console.error(e);
        }
    }

    private startPollingAlarms() {
        this.rxjsPollingTimer = timer(0, 2500);
        this.rxjsPollingTimer.pipe(
            takeUntil(this.destroy$),
            switchMap(() => this.hmiService.getAlarmsValues(<AlarmsFilter>this.dataFilter))
        ).subscribe(result => {
            this.updateAlarmsTable(result);
        });
    }

    private startPollingReports() {
        this.rxjsPollingTimer = timer(0, 60000 * 5);
        this.rxjsPollingTimer.pipe(
            takeUntil(this.destroy$),
            switchMap(() => this.reportsService.getReportsQuery(<ReportsFilter>this.dataFilter))
        ).subscribe(result => {
            this.updateReportsTable(result);
        });
    }

    onRangeChanged(ev) {
        if (this.isEditor) {
            return;
        }
        if (ev) {
            this.range.from = Date.now();
            this.range.to = Date.now();
            this.range.from = new Date(this.range.from).setTime(new Date(this.range.from).getTime() - (TableRangeConverter.TableRangeToHours(ev) * 60 * 60 * 1000));

            this.lastDaqQuery.event = ev;
            this.lastDaqQuery.gid = this.id;
            this.lastDaqQuery.sids = this.getVariableIdsForQuery();
            this.lastDaqQuery.from = this.range.from;
            this.lastDaqQuery.to = this.range.to;
            this.onDaqQuery();
        }
    }

    onDateRange() {
        let dialogRef = this.dialog.open(DaterangeDialogComponent, {
            panelClass: 'light-dialog-container'
        });
        dialogRef.afterClosed().subscribe((dateRange: IDateRange) => {
            if (dateRange) {
                this.range.from = dateRange.start;
                this.range.to = dateRange.end;
                this.lastDaqQuery.gid = this.id;
                this.lastDaqQuery.sids = this.getVariableIdsForQuery();
                this.lastDaqQuery.from = dateRange.start;
                this.lastDaqQuery.to = dateRange.end;
                this.onDaqQuery();
            }
        });
    }

    onDaqQuery(daqQuery?: DaqQuery) {
        if (daqQuery) {
            this.lastDaqQuery = <DaqQuery>Utils.mergeDeep(this.lastDaqQuery, daqQuery);
        }
        // this.lastDaqQuery.chunked = true;
        this.onTimeRange$.next(this.lastDaqQuery);
        if (this.type === TableType.history) {
            this.setLoading(true);
        }
    }

    onRefresh() {
        this.onRangeChanged(this.lastDaqQuery.event);
        this.reloadActive = true;
    }

    setOptions(options: TableOptions): void {
        this.tableOptions = { ...this.tableOptions, ...options };
        this.loadData();
        this.onRangeChanged(this.tableOptions.lastRange || TableRangeType.last1h);
    }

    addValue(variableId: string, dt: number, variableValue: string) {
        if (this.type === TableType.data && this.tagsMap[variableId]) {
            this.tagsMap[variableId].value = variableValue;
            this.tagsMap[variableId].cells.forEach((cell: TableCellData) => {
                const rawValue = GaugeBaseComponent.maskedShiftedValue(variableValue, cell.bitmask);
                cell.stringValue = Utils.formatValue(rawValue?.toString(), cell.valueFormat);
            });
            // update timestamp of all timestamp cells
            this.tagsMap[variableId].rows.forEach((rowIndex: number) => {
                if (this.timestampMap[rowIndex]) {
                    this.timestampMap[rowIndex].forEach((cell: TableCellData) => {
                        cell.stringValue = format(new Date(dt * 1e3), cell.valueFormat || 'YYYY-MM-DD HH:mm:ss');
                    });
                }
            });
        } else if (this.type === TableType.history) {
            if (this.tableOptions.realtime) {
                if (this.addValueInterval && this.pauseMemoryValue[variableId] && Utils.getTimeDifferenceInSeconds(this.pauseMemoryValue[variableId]) < this.addValueInterval) {
                    return;
                }
                this.pauseMemoryValue[variableId] = dt * 1e3;
            }
            this.addRowDataToTable(dt * 1e3, variableId, variableValue);
        }
    }

    setValues(values: { dt: number; value: string }[][]) {
        const rounder = { H: 3600000, m: 60000, s: 1000 };
        const roundIndex = rounder[this.historyDateformat?.[this.historyDateformat?.length - 1]] ?? 1;

        const timestampsSet = new Set<number>();
        const mergedMap = new Map<number, Record<string, string>>(); // timestamp -> { columnId: value }

        // Find columns of type variable in order
        const variableColumns = this.displayedColumns
            .map(colId => this.columnsStyle[colId])
            .filter(col => col.type === TableCellType.variable);

        // xmap construction with rounded timestamps
        values.forEach((variableValues, varIndex) => {
            const column = variableColumns[varIndex];
            if (!column) {
                return;
            }

            variableValues.forEach(entry => {
                const roundedTime = Math.round(entry.dt / roundIndex) * roundIndex;
                if (!mergedMap.has(roundedTime)) {
                    mergedMap.set(roundedTime, {});
                }
                mergedMap.get(roundedTime)![column.id] = entry.value;
                timestampsSet.add(roundedTime);
            });
        });

        const sortedTimestamps = Array.from(timestampsSet).sort((a, b) => b - a);

        // Filling columns with timestamps and values
        const filledRows: Record<string, SimpleCellData>[] = [];

        // For each timestamp, construct the line
        sortedTimestamps.forEach(t => {
            const row: Record<string, SimpleCellData> = {};
            this.displayedColumns.forEach(colId => {
                const column = this.columnsStyle[colId];
                row[colId] = { stringValue: '' };

                if (column.type === TableCellType.timestamp) {
                    row[colId].stringValue = format(new Date(t), column.valueFormat || 'YYYY-MM-DD HH:mm:ss');
                    row[colId].rowIndex = t;
                } else if (column.type === TableCellType.device) {
                    row[colId].stringValue = column.exname;
                } else if (column.type === TableCellType.variable) {
                    const val = mergedMap.get(t)?.[colId] ?? null;
                    if (Utils.isNumeric(val)) {
                        const rawValue = GaugeBaseComponent.maskedShiftedValue(val, column.bitmask);
                        row[colId].stringValue = Utils.formatValue(rawValue?.toString(), column.valueFormat);
                    } else {
                        row[colId].stringValue = val ?? '';
                    }
                }
            });
            filledRows.push(row);
        });

        // Fill in forward missing values for variable columns
        for (const col of variableColumns) {
            let lastValue: string = '';
            for (let i = filledRows.length - 1; i >= 0; i--) {
                if (!filledRows[i][col.id].stringValue) {
                    filledRows[i][col.id].stringValue = lastValue;
                } else {
                    lastValue = filledRows[i][col.id].stringValue;
                }
            }
        }
        this.dataSource.data = filledRows;
        this.bindTableControls();
        setTimeout(() => this.setLoading(false), 500);
        this.reloadActive = false;
    }

    updateAlarmsTable(alrs: AlarmBaseType[]) {
        let rows = [];
        alrs.forEach(alr => {
            let alarm = {
                type: { stringValue: this.priorityText[alr.type] },
                name: { stringValue: alr.name },
                status: { stringValue: this.statusText[alr.status] },
                text: { stringValue: this.languageService.getTranslation(alr.text) ?? alr.text },
                group: { stringValue: this.languageService.getTranslation(alr.group) ?? alr.group },
                ontime: { stringValue: format(new Date(alr.ontime), 'YYYY.MM.DD HH:mm:ss') },
                color: alr.color,
                bkcolor: alr.bkcolor,
                toack: alr.toack
            };
            if (alr.offtime) {
                alarm['offtime'] = { stringValue: format(new Date(alr.offtime), 'YYYY.MM.DD HH:mm:ss') };
            }
            if (alr.acktime) {
                alarm['acktime'] = { stringValue: format(new Date(alr.acktime), 'YYYY.MM.DD HH:mm:ss') };
            }
            if (alr.userack) {
                alarm['userack'] = { stringValue: format(new Date(alr.userack), 'YYYY.MM.DD HH:mm:ss') };
            }
            rows.push(alarm);
        });
        this.dataSource.data = rows;
    }

    updateReportsTable(reports: ReportFile[]) {
        let rows = [];
        reports.forEach(item => {
            let report = {
                name: { stringValue: item.fileName },
                ontime: { stringValue: format(new Date(item.created), 'YYYY.MM.DD HH:mm:ss') },
                deletable: item.deletable,
                fileName: item.fileName
            };
            rows.push(report);
        });
        this.dataSource.data = rows;
    }

    isSelectable(): boolean {
        return this.events?.some(event => event.type === this.eventSelectionType);
    }

    selectRow(row: MatRow) {
        if (this.isSelectable()) {
            this.selectedRow = row;
            this.events.forEach(event => {
                this.runScript(event, this.selectedRow);
            });
        }
    }

    isSelected(row: MatRow) {
        return this.selectedRow === row;
    }

    private runScript(event: GaugeEvent, selected: MatRow) {
        if (event.actparam) {
            let torun = Utils.clone(this.projectService.getScripts().find(dataScript => dataScript.id == event.actparam));
            // 检查 actoptions 和 SCRIPT_PARAMS_MAP 是否存在
            if (event.actoptions && event.actoptions[SCRIPT_PARAMS_MAP]) {
                torun.parameters = <ScriptParam[]>Utils.clone(event.actoptions[SCRIPT_PARAMS_MAP]);
            } else {
                torun.parameters = [];
            }
            torun.parameters.forEach(param => {
                if (Utils.isNullOrUndefined(param.value)) {
                    param.value = selected;
                }
            });
            this.scriptService.runScript(torun).subscribe(result => {
            }, err => {
                console.error(err);
            });
        }
    }

    addRowDataToTable(dt: number, tagId: string, value: string) {
        let row = {};
        let timestapColumnId = null;
        let valueColumnId = null;
        let exnameColumnId = null;
        for (let x = 0; x < this.displayedColumns.length; x++) {
            let column = <TableColumn>this.columnsStyle[this.displayedColumns[x]];
            row[column.id] = <TableCellData>{ stringValue: '' };
            if (column.type === TableCellType.timestamp) {
                timestapColumnId = column.id;
                row[column.id].stringValue = format(new Date(dt), column.valueFormat || 'YYYY-MM-DD HH:mm:ss');
                row[column.id].timestamp = dt;
            } else if (column.variableId === tagId) {
                if (Utils.isNumeric(value)) {
                    const rawValue = GaugeBaseComponent.maskedShiftedValue(value, column.bitmask);
                    row[column.id].stringValue = Utils.formatValue(rawValue?.toString(), column.valueFormat);
                } else {
                    row[column.id].stringValue = value;
                }
                valueColumnId = column.id;
            } else if (column.type === TableCellType.device) {
                row[column.id].stringValue = column.exname;
                exnameColumnId = column.id;
            }
        }
        if (valueColumnId) {
            const firstRow = this.dataSource.data[0];
            if (firstRow && firstRow[timestapColumnId].stringValue === row[timestapColumnId].stringValue) {
                firstRow[valueColumnId].stringValue = row[valueColumnId].stringValue;
                if (exnameColumnId) {
                    firstRow[exnameColumnId].stringValue = row[exnameColumnId].stringValue;
                }
            } else {
                this.dataSource.data.unshift(row);
                const rangeDiff = this.lastDaqQuery.to - this.lastDaqQuery.from;
                this.lastDaqQuery.to = row[timestapColumnId].timestamp;
                this.lastDaqQuery.from = this.lastDaqQuery.to - rangeDiff;
                // remove out of range values
                let count = 0;
                for (let i = this.dataSource.data.length - 1; i >= 0; i--) {
                    if (this.dataSource.data[i][timestapColumnId].timestamp < this.lastDaqQuery.from) {
                        count++;
                    } else {
                        break;
                    }
                }
                if (count) {
                    this.dataSource.data.splice(-count, count);
                }
                this.dataSource = new MatTableDataSource(this.dataSource.data);
            }
        }
    }

    public setProperty(property: any, value: any): boolean {
        if (Utils.isNullOrUndefined(this[property])) {
            return false;
        }
        this[property] = value;
        return true;
    }

    setTableAndData(tableData: TableData) {
        this.setOfSourceTableData = true;
        if (tableData.columns) {
            this.displayedColumns = tableData.columns.map(cln => cln.id);
            this.columnsStyle = tableData.columns;
            tableData.columns.forEach(clnData => {
                let column = clnData;
                column.fontSize = tableData.header?.fontSize;
                column.color = column.color || tableData.header?.color;
                column.background = column.background || tableData.header?.background;
                column.width = column.width || 100;
                column.align = column.align || TableCellAlignType.left;
                this.columnsStyle[column.id] = column;
            });
        }
        if (tableData.rows) {
            this.dataSource = new MatTableDataSource(tableData.rows);
        }
        this.bindTableControls();
    }

    applyFilter(filterValue: string) {
        filterValue = filterValue.trim(); // Remove whitespace
        filterValue = filterValue.toLowerCase(); // MatTableDataSource defaults to lowercase matches
        this.dataSource.filter = filterValue;
    }

    setLoading(load: boolean) {
        if (load) {
            timer(10000).pipe(
                takeUntil(this.destroy$)
            ).subscribe((res) => {
                this.loading = false;
            });
        }
        this.loading = load;
    }

    onExportData() {
        let data = <DataTableContent>{ name: 'data', columns: [] };
        let columns = {};
        Object.values(this.columnsStyle).forEach((column: TableColumn) => {
            columns[column.id] = <DataTableColumn>{ header: `${column.label}`, values: [] };
        });
        this.dataSource.data.forEach(row => {
            Object.keys(row).forEach(id => {
                columns[id].values.push(<TableCellData>row[id].stringValue);
            });
        });
        data.columns = Object.values(columns);
        this.dataService.exportTagsData(data);
    }

    private bindTableControls(): void {
        if (this.type === TableType.history && this.tableOptions.paginator.show) {
            this.dataSource.paginator = this.paginator;
        }
        this.dataSource.sort = this.sort;
        this.dataSource.sortingDataAccessor = (data, sortHeaderId) => data[sortHeaderId].stringValue;
    }

    private loadData() {
        // columns
        let columnIds = [];
        this.columnsStyle = {};
        const columns = this.isAlarmsType() ? this.tableOptions.alarmsColumns : this.isReportsType() ? this.tableOptions.reportsColumns : this.tableOptions.columns;
        columns.forEach(cn => {
            columnIds.push(cn.id);
            this.columnsStyle[cn.id] = cn;
            if (this.type === TableType.history) {
                if (cn.variableId) {
                    this.addColumnToMap(cn);
                }
                if (cn.type === TableCellType.timestamp) {
                    this.historyDateformat = cn.valueFormat;
                }
            }
        });
        this.displayedColumns = columnIds;

        if (this.type === TableType.data) {
            // rows
            this.data = [];
            for (let i = 0; i < this.tableOptions.rows.length; i++) {
                let r = this.tableOptions.rows[i];
                let row = {};
                r.cells.forEach(cell => {
                    if (cell) {
                        row[cell.id] = <TableCellData>{ stringValue: '', rowIndex: i, ...cell };
                        this.mapCellContent(row[cell.id]);
                    }
                });
                this.data.push(row);
            }
        }
        this.dataSource.data = this.data;
        this.withToolbar = this.type === this.tableHistoryType && (this.tableOptions.paginator.show || this.tableOptions.filter.show || this.tableOptions.daterange.show);
    }

    private mapCellContent(cell: TableCellData): void {
        cell.stringValue = '';
        if (cell.type === TableCellType.variable) {
            if (cell.variableId) {
                if (this.isEditor) {
                    cell.stringValue = numeral('123.56').format(cell.valueFormat);
                }
                this.addVariableToMap(cell);
            }
        } else if (cell.type === TableCellType.timestamp) {
            if (this.isEditor) {
                cell.stringValue = format(new Date(0), cell.valueFormat || 'YYYY-MM-DD HH:mm:ss');
            }
            this.addTimestampToMap(cell);
        } else if (cell.type === TableCellType.label) {
            cell.stringValue = cell.label;
        } else if (cell.type === TableCellType.device) {
            cell.stringValue = cell.label;
        }
    }

    private addVariableToMap(cell: TableCellData) {
        if (!this.tagsMap[cell.variableId]) {
            this.tagsMap[cell.variableId] = <ITagMap>{ value: NaN, cells: [], rows: [] };
        }
        this.tagsMap[cell.variableId].cells.push(cell);
        this.tagsMap[cell.variableId].rows.push(cell.rowIndex);
    }

    private addTimestampToMap(cell: TableCellData) {
        if (!this.timestampMap[cell.rowIndex]) {
            this.timestampMap[cell.rowIndex] = [];
        }
        this.timestampMap[cell.rowIndex].push(cell);
    }

    private addColumnToMap(cell: TableColumn) {
        if (!this.tagsColumnMap[cell.variableId]) {
            this.tagsColumnMap[cell.variableId] = [];
        }
        this.tagsColumnMap[cell.variableId].push(cell);
    }

    private getVariableIdsForQuery(): string[] {
        return this.tableOptions.columns
            .filter(col => col.type === TableCellType.variable && col.variableId)
            .map(col => col.variableId)
            .filter(varId => Object.prototype.hasOwnProperty.call(this.tagsColumnMap, varId));
    }

    isAlarmsType(): boolean {
        return this.type === TableType.alarms || this.type === TableType.alarmsHistory;
    }

    isReportsType(): boolean {
        return this.type === TableType.reports;
    }

    onAckAlarm(alarm: any) {
        if (!this.isEditor) {
            this.hmiService.setAlarmAck(alarm.name?.stringValue).subscribe(result => {
            }, error => {
                console.error('Error setAlarmAck', error);
            });
        }
    }

    onDownloadReport(report: ReportFile) {
        this.commandService.getReportFile(report.fileName).subscribe(content => {
            let blob = new Blob([content], { type: 'application/pdf' });
            FileSaver.saveAs(blob, report.fileName);
        }, err => {
            console.error('Download Report File err:', err);
        });
    }

    onRemoveReportFile(report: ReportFile) {
        const userConfirmed = window.confirm(this.translateService.instant('msg.file-remove', { value: report.fileName }));
        if (userConfirmed) {
            this.reportsService.removeReportFile(report.fileName).pipe(
                concatMap(() => timer(2000)),
                catchError((err) => {
                    console.error(`Remove Report File ${report.fileName} err:`, err);
                    return of(null);
                })
            ).subscribe(() => {
                // this.loadDetails(report);
            });
        }
    }

    public static DefaultOptions() {
        let options = <TableOptions>{
            paginator: {
                show: false
            },
            filter: {
                show: false
            },
            daterange: {
                show: false
            },
            realtime: false,
            lastRange: Utils.getEnumKey(TableRangeType, TableRangeType.last1h),
            gridColor: '#E0E0E0',
            header: {
                show: true,
                height: 30,
                fontSize: 12,
                background: '#F0F0F0',
                color: '#757575',
            },
            row: {
                height: 30,
                fontSize: 10,
                background: '#F9F9F9',
                color: '#000000',

            },
            selection: {
                background: '#3059AF',
                color: '#FFFFFF',
                fontBold: true,
            },
            columns: [new TableColumn(Utils.getShortGUID('c_'), TableCellType.timestamp, 'Date/Time'), new TableColumn(Utils.getShortGUID('c_'), TableCellType.label, 'Tags')],
            alarmsColumns: [],
            alarmFilter: { filterA: [], filterB: [], filterC: [] },
            reportsColumns: [],
            reportFilter: { filterA: [] },
            rows: [],
        };
        return options;
    }

    remapVariableIds(options: TableOptions, targetSignalsId: Record<string, string>): void {
        if (!targetSignalsId) {
            return;
        }

        const updateVariableId = (cell: TableCell) => {
            if (cell.variableId && targetSignalsId[cell.variableId]) {
                cell.variableId = targetSignalsId[cell.variableId];
            }
        };

        const processCells = (cells?: TableCell[]) => {
            if (cells) {
                for (const cell of cells) {
                    updateVariableId(cell);
                }
            }
        };

        if (options.columns) {
            processCells(options.columns);
        }
        if (options.alarmsColumns) {
            processCells(options.alarmsColumns);
        }
        if (options.reportsColumns) {
            processCells(options.reportsColumns);
        }
        if (options.rows) {
            for (const row of options.rows) {
                processCells(row.cells);
            }
        }
    }
}

export class TableCellData extends TableCell {
    rowIndex?: number;
    stringValue: string;
}

interface SimpleCellData {
    rowIndex?: number;
    stringValue: string;
    timestamp?: number;
}

interface ITagMap {
    value: number;
    cells: TableCellData[];
    rows: number[];
}


export class TableRangeConverter {
    static TableRangeToHours(crt: TableRangeType) {
        let types = Object.keys(TableRangeType);
        if (crt === types[0]) {         // TableRangeType.last1h) {
            return 1;
        } else if (crt === types[1]) {  // TableRangeType.last1d) {
            return 24;
        } else if (crt === types[2]) {  // TableRangeType.last3d) {
            return 24 * 3;
        }
        return 0;
    }
}

interface TableMapValueDictionary {
    [key: string]: number;
}

/**
 * Interface definition for setTableAndData from script
 */
interface TableData {
    paginator?: TableDataPaginatorOptions;
    filter?: TableDataFilterOptions;
    gridColor?: string;
    header?: TableDataHeaderStyle;
    rowStyle?: TableDataRowStyle;
    columns?: TableDataColumnData[];
    rows?: TableDataRow[];
}

interface TableDataPaginatorOptions {
    show: boolean;
    pageSize: number;
}

interface TableDataFilterOptions {
    show: boolean;
}

interface TableDataHeaderStyle {
    show: boolean;
    height: number;
    fontSize: number;
    background: string;
    color: string;
}

interface TableDataRowStyle {
    height: number;
    fontSize: number;
    background: string;
    color: string;
}

interface TableDataColumnData {
    id: string;
    label: string;
    align?: string;
    width?: number;
    color?: string;
    fontSize: number;
    background: string;
}

interface TableDataRow {
    cells: TableDataCell[];
}

interface TableDataCell {
    value: string;
}
